import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, Printer, Download, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  const effectiveDate = "January 1, 2026";

  // Table of contents sections
  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'information-we-collect', title: 'Information We Collect' },
    { id: 'how-we-use', title: 'How We Use Your Information' },
    { id: 'how-we-share', title: 'How We Share Information' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'your-rights', title: 'Your Rights and Choices' },
    { id: 'special-considerations', title: 'Special Considerations' },
    { id: 'international', title: 'International Users' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'changes', title: 'Changes to This Policy' },
    { id: 'contact', title: 'Contact Us' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);

      // Update active section based on scroll position
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i].element;
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(sectionElements[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <header className="bg-[#556B2F] text-white py-6 print:bg-white print:text-[#556B2F]">
        <div className="max-w-6xl mx-auto px-6">
          <Link to="/" className="text-2xl font-bold hover:opacity-90 transition-opacity">
            One Generation to Next
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation - Hidden on print */}
          <aside className="lg:w-72 flex-shrink-0 print:hidden">
            <div className="sticky top-8">
              <nav className="bg-[#F2F2F2] rounded-lg border border-[#CCCCCC] p-6">
                <h2 className="text-lg font-semibold text-[#556B2F] mb-4">Contents</h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`text-left w-full px-3 py-2 rounded-md text-sm transition-colors ${
                          activeSection === section.id
                            ? 'bg-[#556B2F] text-white'
                            : 'text-[#333333] hover:bg-[#6B8E23] hover:text-white'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Actions */}
                <div className="mt-6 pt-6 border-t border-[#CCCCCC] space-y-3">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Printer size={16} />
                    Print this page
                  </button>
                  <a
                    href="/documents/1G2N_Privacy_Policy.pdf"
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Download size={16} />
                    Download PDF
                  </a>
                  <a
                    href="mailto:privacy@1g2n.com"
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Mail size={16} />
                    Contact us
                  </a>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <article className="bg-white rounded-lg border border-[#CCCCCC] p-8 lg:p-12 print:border-none print:p-0">
              {/* Title */}
              <header className="mb-10 pb-8 border-b border-[#CCCCCC]">
                <h1 className="text-4xl lg:text-5xl font-bold text-[#556B2F] mb-4">
                  Privacy Policy
                </h1>
                <p className="text-lg text-[#666666]">
                  Effective Date: {effectiveDate}
                </p>
              </header>

              {/* Content */}
              <div className="prose prose-lg max-w-none text-[#333333]">

                {/* Introduction */}
                <section id="introduction" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Introduction</h2>
                  <p className="mb-4 leading-relaxed">
                    Welcome to One Generation to Next ("1G2N," "we," "us," or "our"). We understand that the memories, stories, and wisdom you share with us are precious and deeply personal. This Privacy Policy explains how we collect, use, protect, and share information when you use our family legacy platform.
                  </p>
                  <p className="leading-relaxed">
                    Our mission is to help families preserve and transfer wisdom across generations. We take this responsibility seriously and are committed to protecting your privacy while enabling meaningful connections within your family.
                  </p>
                </section>

                {/* Information We Collect */}
                <section id="information-we-collect" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Information We Collect</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Information You Provide Directly</h3>
                  <p className="mb-4 leading-relaxed">
                    When you use One Generation to Next, you may provide us with the following types of information:
                  </p>

                  <h4 className="text-lg font-semibold text-[#4A5D23] mb-3 mt-6">Account Information</h4>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Name, email address, and password</li>
                    <li>Profile photograph</li>
                    <li>Date of birth and family relationship information</li>
                    <li>Contact preferences</li>
                  </ul>

                  <h4 className="text-lg font-semibold text-[#4A5D23] mb-3">Family Stories and Content</h4>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Written stories, memories, and life lessons</li>
                    <li>Photographs and videos</li>
                    <li>Audio recordings, including voice messages</li>
                    <li>Family recipes and instructions</li>
                    <li>Family tree information and relationships</li>
                  </ul>

                  <h4 className="text-lg font-semibold text-[#4A5D23] mb-3">Voice and Audio Data</h4>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Voice recordings for stories and messages</li>
                    <li>Voice training samples for our Voiceprint Memories feature</li>
                    <li>Audio used for AI voice model training (with explicit consent)</li>
                  </ul>

                  <h4 className="text-lg font-semibold text-[#4A5D23] mb-3">Time Capsules and Legacy Content</h4>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Messages scheduled for future delivery</li>
                    <li>Legacy letters and instructions</li>
                    <li>Inheritance designations and preferences</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Information Collected Automatically</h3>
                  <p className="mb-4 leading-relaxed">When you use our services, we automatically collect:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Device information (type, operating system, browser)</li>
                    <li>IP address and approximate location</li>
                    <li>Usage patterns and interaction data</li>
                    <li>Log data and error reports</li>
                  </ul>
                </section>

                {/* How We Use Your Information */}
                <section id="how-we-use" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">How We Use Your Information</h2>
                  <p className="mb-6 leading-relaxed">We use the information we collect to:</p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Provide Our Core Services</h3>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Store and organize your family stories and content</li>
                    <li>Enable sharing with family members you designate</li>
                    <li>Build and display your family tree</li>
                    <li>Deliver time capsule messages at scheduled times</li>
                    <li>Execute inheritance transfers according to your wishes</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Power AI Features</h3>
                  <p className="mb-4 leading-relaxed">With your explicit consent, we use your data to:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Create voice models for Voiceprint Memories</li>
                    <li>Enable AI Conversation Simulation features</li>
                    <li>Generate prompts and suggestions for storytelling</li>
                    <li>Provide intelligent search and categorization</li>
                  </ul>
                  <div className="bg-[#F5F5DC] border-l-4 border-[#E65100] p-4 my-6">
                    <p className="text-[#333333] font-medium">
                      <strong>Important:</strong> AI features that use your voice or create simulations are optional and require separate, explicit consent. You maintain full control over whether these features are enabled.
                    </p>
                  </div>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Improve and Protect Our Services</h3>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Analyze usage to improve features and user experience</li>
                    <li>Detect and prevent fraud, abuse, and security threats</li>
                    <li>Provide customer support</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                {/* How We Share Information */}
                <section id="how-we-share" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">How We Share Information</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">With Your Family</h3>
                  <p className="mb-4 leading-relaxed">
                    The core purpose of One Generation to Next is to share memories and wisdom within families. You control what is shared and with whom through our privacy settings:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li><strong>Private:</strong> Only you can see the content</li>
                    <li><strong>Family Only:</strong> Shared with designated family members</li>
                    <li>All content sharing is based on your explicit choices</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Service Providers</h3>
                  <p className="mb-4 leading-relaxed">We work with trusted third parties who help us operate our services:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Cloud storage providers (for secure data storage)</li>
                    <li>AI processing services (for voice and content features)</li>
                    <li>Payment processors (for subscription management)</li>
                    <li>Analytics providers (for service improvement)</li>
                  </ul>
                  <p className="leading-relaxed">
                    These providers are contractually obligated to protect your information and may only use it to provide services on our behalf.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Legal Requirements</h3>
                  <p className="mb-4 leading-relaxed">
                    We may disclose information when required by law, legal process, or government request, or when necessary to protect the rights, safety, or property of our users or the public.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Business Transfers</h3>
                  <p className="leading-relaxed">
                    If One Generation to Next is involved in a merger, acquisition, or sale of assets, your information may be transferred. We will provide notice before your information becomes subject to a different privacy policy.
                  </p>
                </section>

                {/* Data Security */}
                <section id="data-security" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Data Security</h2>
                  <p className="mb-4 leading-relaxed">
                    We implement comprehensive security measures to protect your precious family content:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Encryption of data in transit and at rest</li>
                    <li>Secure authentication and access controls</li>
                    <li>Regular security audits and monitoring</li>
                    <li>Employee training on data protection</li>
                    <li>Incident response procedures</li>
                  </ul>
                  <p className="leading-relaxed">
                    While we strive to protect your information, no method of transmission or storage is completely secure. We encourage you to use strong passwords and protect your account credentials.
                  </p>
                </section>

                {/* Your Rights and Choices */}
                <section id="your-rights" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Your Rights and Choices</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Access and Portability</h3>
                  <p className="mb-6 leading-relaxed">
                    You can access, download, and export your content at any time. We offer multiple export formats including digital archives to ensure your family legacy is preserved in the format you prefer.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Correction and Deletion</h3>
                  <p className="mb-4 leading-relaxed">
                    You can edit or delete your content at any time through your account. When you delete content:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Content is removed from active systems within 30 days</li>
                    <li>Backup copies may be retained for up to 90 days</li>
                    <li>Some information may be retained as required by law</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">AI Feature Controls</h3>
                  <p className="mb-4 leading-relaxed">You have specific controls over AI features:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Opt-in/opt-out of voice model training</li>
                    <li>Enable or disable AI conversation simulation</li>
                    <li>Request deletion of AI models created from your data</li>
                    <li>Review what data is used for AI features</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Communication Preferences</h3>
                  <p className="leading-relaxed">
                    You can manage your notification and communication preferences in your account settings, including email notifications and family sharing alerts.
                  </p>
                </section>

                {/* Special Considerations */}
                <section id="special-considerations" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Special Considerations</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Inheritance and Succession</h3>
                  <p className="mb-6 leading-relaxed">
                    One Generation to Next includes features to transfer your content to designated family members. You can specify who should receive access to your content and under what conditions. We handle these transitions with care and respect for your wishes.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Deceased Users</h3>
                  <p className="mb-6 leading-relaxed">
                    We understand the sensitive nature of preserving memories after a loved one passes. Content from deceased users is handled according to their inheritance settings. Family members with appropriate documentation may request account access or closure.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Children's Privacy</h3>
                  <p className="mb-4 leading-relaxed">
                    Our services are designed for adults (18 and older). We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us immediately.
                  </p>
                  <p className="leading-relaxed">
                    Family members may include stories about younger family members, but account creation and content uploading requires adult supervision for users under 18.
                  </p>
                </section>

                {/* International Users */}
                <section id="international" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">International Users</h2>
                  <p className="mb-6 leading-relaxed">
                    One Generation to Next operates globally to serve diaspora families. Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for international data transfers.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">For European Users (GDPR)</h3>
                  <p className="mb-4 leading-relaxed">
                    If you are located in the European Economic Area, you have additional rights including:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Right to access your personal data</li>
                    <li>Right to rectification of inaccurate data</li>
                    <li>Right to erasure ("right to be forgotten")</li>
                    <li>Right to restrict processing</li>
                    <li>Right to data portability</li>
                    <li>Right to object to processing</li>
                    <li>Right to withdraw consent</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">For California Residents (CCPA)</h3>
                  <p className="leading-relaxed">
                    California residents have specific rights under the California Consumer Privacy Act, including the right to know what personal information is collected and how it is used, and the right to request deletion.
                  </p>
                </section>

                {/* Data Retention */}
                <section id="data-retention" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Data Retention</h2>
                  <p className="mb-4 leading-relaxed">
                    We retain your information for as long as your account is active or as needed to provide services. Because our platform is designed for long-term legacy preservation:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Active content is retained indefinitely while your account is active</li>
                    <li>Time capsules are retained until their scheduled delivery date</li>
                    <li>Inherited content is transferred according to your instructions</li>
                    <li>Deleted content is removed according to our deletion schedule</li>
                  </ul>
                  <p className="leading-relaxed">
                    You may request complete account deletion at any time by contacting our support team.
                  </p>
                </section>

                {/* Changes to This Policy */}
                <section id="changes" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Changes to This Policy</h2>
                  <p className="leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our platform and, where appropriate, by email. Your continued use of our services after changes become effective constitutes acceptance of the updated policy.
                  </p>
                </section>

                {/* Contact Us */}
                <section id="contact" className="mb-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Contact Us</h2>
                  <p className="mb-6 leading-relaxed">
                    If you have questions about this Privacy Policy or our privacy practices, please contact us:
                  </p>
                  <div className="bg-[#F2F2F2] rounded-lg p-6 border border-[#CCCCCC]">
                    <p className="font-semibold text-[#556B2F] text-lg mb-2">One Generation to Next</p>
                    <p className="mb-1">
                      <strong>Email:</strong>{' '}
                      <a href="mailto:privacy@1g2n.com" className="text-[#6B8E23] hover:underline">
                        privacy@1g2n.com
                      </a>
                    </p>
                    <p className="mb-4">
                      <strong>Address:</strong> [Your Business Address]
                    </p>
                    <p className="text-sm text-[#666666]">
                      For data protection inquiries in the European Union, you may also contact our Data Protection Officer at{' '}
                      <a href="mailto:dpo@1g2n.com" className="text-[#6B8E23] hover:underline">
                        dpo@1g2n.com
                      </a>
                    </p>
                  </div>
                </section>

              </div>
            </article>

            {/* Related Links */}
            <div className="mt-8 p-6 bg-[#F2F2F2] rounded-lg border border-[#CCCCCC] print:hidden">
              <h3 className="font-semibold text-[#556B2F] mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/terms"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/cookies"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Cookie Policy
                </Link>
                <Link
                  to="/data-request"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Submit a Data Request
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#556B2F] text-white p-3 rounded-full shadow-lg hover:bg-[#6B8E23] transition-colors print:hidden"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}

      {/* Footer */}
      <footer className="bg-[#556B2F] text-white py-8 mt-12 print:hidden">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm opacity-90">
            &copy; {new Date().getFullYear()} One Generation to Next. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:underline opacity-90 hover:opacity-100">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:underline opacity-90 hover:opacity-100">
              Terms of Service
            </Link>
            <Link to="/contact" className="hover:underline opacity-90 hover:opacity-100">
              Contact Us
            </Link>
          </div>
        </div>
      </footer>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            font-size: 12pt;
            line-height: 1.5;
          }
          .prose {
            max-width: none;
          }
          h1 {
            font-size: 24pt;
          }
          h2 {
            font-size: 18pt;
            page-break-after: avoid;
          }
          h3 {
            font-size: 14pt;
            page-break-after: avoid;
          }
          ul, p {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;
